import type { UiSchema } from '@rjsf/utils';
import { schema as initSchema } from '@unifyapps/defs/types/event/schema/onPusherInit';
import {
  schema as subscribeSchema,
  uiSchema as subscribeUiSchema,
} from '@unifyapps/defs/types/event/schema/onPusherSubscribe';
import {
  schema as unsubscribeSchema,
  uiSchema as unsubscribeUiSchema,
} from '@unifyapps/defs/types/event/schema/onPusherUnsubscribe';
import type { PusherChannelConfig } from '@unifyapps/defs/types/interface';
import type { PageEventType } from '@unifyapps/defs/types/page';
import { PUSHER_CONNECTION_CALLBACK_TYPES, PUSHER_SUBSCRIPTION_CALLBACK_TYPES } from './callbacks';
import { PUSHER_METHOD_TYPES } from './methods';

export const PUSHER_CHANNEL_PAYLOAD_PROPERTY = {
  channel: { type: 'string', title: 'Channel', description: 'The channel this event fired on.' },
} as const;

export function withChannelInPayload(event: PageEventType): PageEventType {
  const payload = event.payload as { properties?: Record<string, unknown> } | undefined;
  return {
    ...event,
    payload: {
      type: 'object',
      ...payload,
      properties: { ...payload?.properties, ...PUSHER_CHANNEL_PAYLOAD_PROPERTY },
    },
  };
}

export function getPusherMethodPageEvents(
  channels: PusherChannelConfig[] = [],
): (PageEventType & { payloadUiSchema?: UiSchema })[] {
  const channelOneOf =
    channels.length > 0
      ? channels.map((channel) => ({
          const: channel.channelName,
          title: channel.label || channel.channelName,
        }))
      : undefined;

  const subscribePayloadSchema = {
    ...subscribeSchema,
    properties: {
      ...subscribeSchema.properties,
      channel: {
        ...subscribeSchema.properties.channel,
        ...(channelOneOf ? { oneOf: channelOneOf } : {}),
      },
    },
  };
  const unsubscribePayloadSchema = {
    ...unsubscribeSchema,
    properties: {
      ...unsubscribeSchema.properties,
      channel: {
        ...unsubscribeSchema.properties.channel,
        ...(channelOneOf ? { oneOf: channelOneOf } : {}),
      },
    },
  };

  return [
    {
      eventType: PUSHER_METHOD_TYPES.init,
      label: 'Initialize',
      description:
        'Resolve the connection identity and open the Pusher connection. Must run once, before Subscribe — typically wired to run on page load, with the auth value bound to the current user.',
      payload: initSchema,
    },
    {
      eventType: PUSHER_METHOD_TYPES.subscribe,
      label: 'Subscribe',
      description:
        'Subscribe to a channel configured in Pusher settings or enter a custom one, and start listening for its configured events.',
      payload: subscribePayloadSchema,
      payloadUiSchema: subscribeUiSchema,
    },
    {
      eventType: PUSHER_METHOD_TYPES.unsubscribe,
      label: 'Unsubscribe',
      description:
        'Stop listening for events on the specified channel. The connection stays open — run Subscribe again to resume.',
      payload: unsubscribePayloadSchema,
      payloadUiSchema: unsubscribeUiSchema,
    },
  ];
}

export const PUSHER_CALLBACK_PAGE_EVENTS = [
  {
    eventType: PUSHER_SUBSCRIPTION_CALLBACK_TYPES.subscriptionSucceeded,
    label: 'On Subscription Successful',
    description:
      'Fires once a channel subscription is confirmed and its configured events are being listened for.',
    payload: { type: 'object', properties: PUSHER_CHANNEL_PAYLOAD_PROPERTY },
  },
  {
    eventType: PUSHER_SUBSCRIPTION_CALLBACK_TYPES.subscriptionError,
    label: 'On Subscription Error',
    description:
      'Fires when a channel subscription fails — typically an auth rejection from the configured Auth Endpoint.',
    payload: { type: 'object', properties: PUSHER_CHANNEL_PAYLOAD_PROPERTY },
  },
  {
    eventType: PUSHER_SUBSCRIPTION_CALLBACK_TYPES.subscriptionCount,
    label: 'On Subscription Count Change',
    description:
      'Fires whenever the number of clients subscribed to a channel changes. Must be enabled for that channel from the Pusher dashboard — otherwise this never fires.',
    payload: {
      type: 'object',
      properties: {
        ...PUSHER_CHANNEL_PAYLOAD_PROPERTY,
        subscription_count: { type: 'number', title: 'Subscription Count' },
      },
    },
  },
] satisfies PageEventType[];

export const PUSHER_CONNECTION_CALLBACK_PAGE_EVENTS = [
  {
    eventType: PUSHER_CONNECTION_CALLBACK_TYPES.connected,
    label: 'On Connected',
    description:
      'Fires once the underlying connection to Pusher is live. This only confirms the base connection — it does not mean a channel is subscribed yet, use On Subscription Successful for that.',
  },
  {
    eventType: PUSHER_CONNECTION_CALLBACK_TYPES.disconnected,
    label: 'On Disconnected',
    description:
      'Fires once the connection has fully closed — whether from Unsubscribe/interface teardown, after Pusher gives up retrying, or an out-of-band connection error. Use to disable Pusher-dependent UI.',
  },
] satisfies PageEventType[];
